import { useReducedMotion } from '@/lib/useReducedMotion';
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { ChevronLeft } from 'lucide-react';
import type { LinkItem, LinkSection } from '@/data/links';
import type { SectionWithLinks } from '@/lib/usePublicData';
import { GlowCard } from './GlowCard';
import { LinkPreview } from './LinkPreview';
import { emitSectionChange, emitZap, rectCenter, restingCenter } from './zapEvents';

/** Chorus concept hues, one per tab; the lanes and streams read them from data-rd-hue. */
const HUES = ['#E4705E', '#7C9CFF', '#3FBFA0', '#D9A441', '#C084FC'];

interface RedesignTabsProps {
  sections: (LinkSection | SectionWithLinks)[];
  visible: boolean;
  /** Desktop: one stacked column plus the iris preview. Otherwise two stacked columns. */
  withPreview: boolean;
  onLinkClick?: (id: string) => void;
}

interface StackColumnProps {
  links: LinkItem[];
  visible: boolean;
  openId: string | null;
  onOpen: (id: string | null) => void;
  onLinkHover?: (link: LinkItem, el: HTMLElement) => void;
  onColumnLeave?: () => void;
  markTargets?: boolean;
  onLinkClick?: (id: string) => void;
}

const StackColumn = ({ links, visible, openId, onOpen, onLinkHover, onColumnLeave, markTargets, onLinkClick }: StackColumnProps) => {
  const colRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={colRef}
      className="relative w-full"
      data-rd-cards={markTargets || undefined}
      onMouseLeave={() => {
        if (!window.matchMedia('(pointer: coarse)').matches) onOpen(null);
        onColumnLeave?.();
      }}
    >
      {links.map((link, i) => (
        <GlowCard
          key={link.id}
          {...link}
          index={i}
          total={links.length}
          visible={visible}
          open={openId === link.id}
          onLinkClick={onLinkClick}
          onOpen={(next) => {
            const id = next ? link.id : null;
            onOpen(id);
            const el = colRef.current?.children[i];
            if (id && el instanceof HTMLElement) onLinkHover?.(link, el);
          }}
        />
      ))}
    </div>
  );
};

export const RedesignTabs = ({ sections, visible, withPreview, onLinkClick }: RedesignTabsProps) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const activeTab = Math.max(0, sections.findIndex((section) => section.id === activeId));
  const activeSectionId = sections[activeTab]?.id;
  const selectTab = (index: number, focus = false) => {
    const section = sections[index];
    if (!section) return;
    setActiveId(section.id);
    if (focus) {
      tabRefs.current[index]?.focus();
      tabRefs.current[index]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  };
  const reduced = useReducedMotion();
  const [twoColumns, setTwoColumns] = useState(() => window.matchMedia('(min-width: 640px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 640px)');
    const update = () => setTwoColumns(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const [previewPaused, setPreviewPaused] = useState(false);
  const previewInteraction = useRef(false);
  const [cardsVisible, setCardsVisible] = useState(visible);
  const [headerVisible, setHeaderVisible] = useState(visible);
  const [preview, setPreview] = useState<{ link: LinkItem; el: HTMLElement } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [moreTabs, setMoreTabs] = useState(false);

  const previousSection = useRef(activeSectionId);
  useEffect(() => {
    setHeaderVisible(visible);
    setCardsVisible(visible);
  }, [visible]);

  useEffect(() => {
    if (previousSection.current === activeSectionId) {
      if (reduced) setCardsVisible(visible);
      return;
    }
    previousSection.current = activeSectionId;
    setPreview(null);
    setOpenId(null);
    emitSectionChange();
    if (reduced) { setCardsVisible(true); return; }
    setCardsVisible(false);
    const timer = setTimeout(() => setCardsVisible(true), 150);
    return () => clearTimeout(timer);
  }, [activeSectionId, reduced, visible]);

  useLayoutEffect(() => {
    const place = () => {
      const el = tabRefs.current[activeTab];
      if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    place();
    window.addEventListener('resize', place);
    document.fonts?.ready.then(place);
    return () => window.removeEventListener('resize', place);
  }, [activeTab, sections]);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => {
      const max = el.scrollWidth - el.clientWidth;
      setMoreTabs(max > 12 && Math.abs(el.scrollLeft) < max - 12);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [sections.length]);

  const community = sections.find((s) => s.id === 'community' || s.title.includes('קהילות')) ?? sections[0];
  const featuredLinks = community?.links ?? [];
  const [featuredIdx, setFeaturedIdx] = useState(0);
  useEffect(() => {
    if (preview || previewPaused || reduced || !withPreview || featuredLinks.length < 2) return;
    const id = setInterval(() => setFeaturedIdx((i) => (i + 1) % featuredLinks.length), 5000);
    return () => clearInterval(id);
  }, [preview, previewPaused, reduced, withPreview, featuredLinks.length]);
  const featured = featuredLinks.length ? featuredLinks[featuredIdx % featuredLinks.length] : null;

  const showLink = useCallback((link: LinkItem, el: HTMLElement) => {
    clearTimeout(hideTimer.current);
    setPreview((p) => (p?.link.id === link.id ? p : { link, el }));
  }, []);
  const scheduleHide = useCallback((ms: number) => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (!previewInteraction.current) setPreview(null);
    }, ms);
  }, []);
  const onPreviewInteraction = useCallback((paused: boolean) => {
    previewInteraction.current = paused;
    setPreviewPaused(paused);
    if (paused) clearTimeout(hideTimer.current);
    else scheduleHide(700);
  }, [scheduleHide]);

  const currentSection = sections[activeTab];
  if (!currentSection) return null;
  const links = currentSection.links || [];
  const hue = HUES[activeTab % HUES.length];

  return (
    <div className={`w-full mx-auto px-3 sm:px-4 ${withPreview ? 'max-w-6xl' : 'max-w-4xl'}`} dir="rtl">
      <div
        className={`relative mb-9 border-b border-white/[0.08] transition-all duration-500 ${
          headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}
      >
        <div
          ref={scrollerRef}
          role="tablist"
          aria-label="קטגוריות"
          className="relative flex gap-1 overflow-x-auto scrollbar-hide sm:gap-2"
        >
          {sections.map((section, i) => {
            const isActive = i === activeTab;
            const tabHue = HUES[i % HUES.length];
            return (
              <button
                key={section.id}
                ref={(el) => { tabRefs.current[i] = el; }}
                role="tab"
                id={`rd-tab-${section.id}`}
                aria-selected={isActive}
                aria-controls={`rd-panel-${section.id}`}
                tabIndex={isActive ? 0 : -1}
                onKeyDown={(event) => {
                  const next = event.key === 'ArrowLeft' ? (i + 1) % sections.length : event.key === 'ArrowRight' ? (i - 1 + sections.length) % sections.length : event.key === 'Home' ? 0 : event.key === 'End' ? sections.length - 1 : null;
                  if (next !== null) { event.preventDefault(); selectTab(next, true); }
                }}
                data-rd-tab
                data-rd-hue={tabHue}
                onClick={() => selectTab(i)}
                onPointerEnter={(e) => {
                  const deck = document.querySelector('[data-rd-zap]');
                  if (!deck) return;
                  emitZap(rectCenter(e.currentTarget), restingCenter(deck as HTMLElement));
                }}
                className={`group flex-none flex items-center gap-2 px-2.5 pt-2 pb-3.5 text-[13px] transition-colors duration-300 sm:gap-2.5 sm:px-4 sm:text-[15px] ${
                  isActive ? 'text-white font-medium' : 'text-white/50 hover:text-white/80'
                }`}
              >
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full transition-[box-shadow,opacity,transform] duration-300"
                  style={{
                    background: tabHue,
                    opacity: isActive ? 1 : 0.55,
                    transform: isActive ? 'scale(1.25)' : 'none',
                    boxShadow: isActive ? `0 0 10px ${tabHue}` : 'none'
                  }}
                />
                <span className="hidden whitespace-nowrap sm:inline">{section.title}</span>
                <span className="flex flex-col items-center leading-[1.15] sm:hidden">
                  {section.title.split(' ').map((word) => <span key={word}>{word}</span>)}
                </span>
                <span className={`rd-mono text-[11px] tabular-nums ${isActive ? 'text-white/60' : 'text-white/30'}`}>
                  {section.links.length}
                </span>
              </button>
            );
          })}
          <span
            aria-hidden="true"
            className="absolute bottom-0 h-[2px] rounded-full transition-[left,width,background,box-shadow] duration-500 ease-[cubic-bezier(.2,.75,.2,1)]"
            style={{
              left: indicator.left + 10,
              width: Math.max(0, indicator.width - 20),
              background: `linear-gradient(90deg, transparent, ${hue}, transparent)`,
              boxShadow: `0 0 12px ${hue}`
            }}
          />
        </div>
        {moreTabs && (
          <button
            type="button"
            aria-label="עוד קטגוריות"
            onClick={() => scrollerRef.current?.scrollBy({ left: -140, behavior: reduced ? 'auto' : 'smooth' })}
            className="absolute bottom-2 left-0 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-white/15 bg-[#060B1E]/80 text-white/80"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      <div role="tabpanel" id={`rd-panel-${currentSection.id}`} aria-labelledby={`rd-tab-${currentSection.id}`}>
      {withPreview ? (
        <div className="grid grid-cols-[minmax(0,560px)_minmax(300px,1fr)] gap-x-[clamp(48px,6vw,88px)] pb-8">
          <StackColumn
            key={currentSection.id}
            links={links}
            visible={cardsVisible}
            openId={openId}
            onOpen={setOpenId}
            markTargets
            onLinkClick={onLinkClick}
            onLinkHover={showLink}
            onColumnLeave={() => scheduleHide(900)}
          />
          <div className="sticky top-6 self-start">
            <LinkPreview
              link={preview?.link ?? null}
              originEl={preview?.el ?? null}
              sectionTitle={currentSection.title}
              featured={featured}
              featuredTitle={community?.title ?? ''}
              featuredIndex={featuredIdx % Math.max(1, featuredLinks.length)}
              featuredCount={featuredLinks.length}
              variant={5}
              onLinkClick={onLinkClick}
              onInteractionChange={onPreviewInteraction}
              onPointerEnter={() => clearTimeout(hideTimer.current)}
              onPointerLeave={() => scheduleHide(700)}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto pb-8">
          {!twoColumns ? <StackColumn links={links} visible={cardsVisible} openId={openId} onOpen={setOpenId} onLinkClick={onLinkClick} markTargets /> : <><StackColumn links={links.filter((_, i) => i % 2 === 0)} visible={cardsVisible} openId={openId} onOpen={setOpenId} onLinkClick={onLinkClick} markTargets />
          <StackColumn links={links.filter((_, i) => i % 2 === 1)} visible={cardsVisible} openId={openId} onOpen={setOpenId} onLinkClick={onLinkClick} markTargets /></>}
        </div>
      )}
      </div>
      {sections.filter((section) => section.id !== currentSection.id).map((section) => <div key={section.id} role="tabpanel" id={`rd-panel-${section.id}`} aria-labelledby={`rd-tab-${section.id}`} hidden />)}
    </div>
  );
};
